import React from 'react';
import { useSelector } from 'react-redux';
import PropTypes from 'prop-types';
import { DatePickerVertical } from '../../../components/DatePicker/DatePickerVertical';
import { CalendarType, SlideType } from '../../../models';
import { Fragment } from './Fragment';
import { FragmentBody } from './FragmentBody';
import { NextButton } from './NextButton';
import { ABUtils, DateUtils, CMS, Config } from '../../../utils';
import { ConnectedSegmentDateRoundTrip } from '../Segment/SegmentDateRoundTrip';
import { DatePickerLegends } from '../../../components/DatePicker/DatePickerLegends';
import { ConnectedOfferOneWay } from './OfferOneWay';
import { ConnectedFaresTrackingCalculator } from '../../../components/Calendar/Context/FaresTrackingCalculator';
import { DatesFragmentHeader } from './DatesFragmentHeader';
import { canDisplayFaresInCalendar } from '../../../redux/currentSearch/selectors';

export const DatesRoundTripFragmentInner = ({
    onSelectValue,
    isDone,
    dates,
    value,
    minDate,
    displayFaresInCalendar,
    offerOneWay,
}) => {
    const canDisplayFares = useSelector(canDisplayFaresInCalendar);
    const { isCssVariationMobile } = ABUtils.getABs();
    const hasFirstDate = !!DateUtils.stringToDate(dates[0]);
    const hasSecondDate = !!DateUtils.stringToDate(dates[1]);
    const stop = !hasFirstDate || hasSecondDate || canDisplayFares;
    const isReturn = hasFirstDate && !hasSecondDate;
    const direction = Config.get('isRtl') ? 'rtl' : 'ltr';
    const formattedDates = dates.map((date) => DateUtils.stringToDate(date));
    const onChangeDates = (value, stop) => {
        const areSameDatesInHotel =
            isReturn && !offerOneWay && DateUtils.compare(formattedDates[0], value) === 0;
        if (areSameDatesInHotel) {
            return;
        }
        return onSelectValue(value, stop);
    };

    return (
        <React.Fragment>
            <DatesFragmentHeader />
            <FragmentBody padding={false}>
                {!isCssVariationMobile && (
                    <div className="odf-space-outer-m odf-text-soft odf-text-left odf-text-xl">
                        <CMS id="flightsManager.selection.date.title" />
                    </div>
                )}
                <DatePickerVertical
                    dates={formattedDates}
                    displayFaresInCalendar={displayFaresInCalendar}
                    value={value}
                    minDate={minDate}
                    onChange={(value) => onChangeDates(value, stop)}
                    isDatesRangeShown
                    type={isReturn ? CalendarType.RETURN : CalendarType.DEPARTURE}
                    direction={direction}
                />
            </FragmentBody>
            {offerOneWay && <ConnectedOfferOneWay dates={formattedDates} />}
            <div className="odf-box" style={{ borderRadius: 0 }}>
                <DatePickerLegends displayFaresInCalendar={canDisplayFares} />
            </div>
            {isDone && !canDisplayFares && (
                <ConnectedFaresTrackingCalculator trackType="continue_calendar">
                    {({ extraTracking }) => (
                        <NextButton
                            extraTracking={extraTracking}
                            testId="dates-roundtrip-next-btn"
                        />
                    )}
                </ConnectedFaresTrackingCalculator>
            )}
        </React.Fragment>
    );
};

DatesRoundTripFragmentInner.propTypes = {
    dates: PropTypes.arrayOf(Date).isRequired,
    displayFaresInCalendar: PropTypes.bool.isRequired,
    isDone: PropTypes.bool.isRequired,
    minDate: PropTypes.instanceOf(Date).isRequired,
    onSelectValue: PropTypes.func.isRequired,
    value: PropTypes.instanceOf(Date),
    offerOneWay: PropTypes.bool,
};

DatesRoundTripFragmentInner.defaultProps = {
    value: null,
    offerOneWay: true,
};

export const DatesRoundTripFragment = () => (
    <ConnectedSegmentDateRoundTrip>
        {({ dates, minDate, setValue, value, displayFaresInCalendar, offerOneWay }) => {
            const isDone = !!dates[0] && !!dates[1];
            const formattedDates = dates.map((date) => DateUtils.stringToDate(date));

            return (
                <Fragment id={SlideType.DATE} isDone={isDone} isEmpty={!isDone} setValue={setValue}>
                    {({ onSelectValue }) => (
                        <DatesRoundTripFragmentInner
                            onSelectValue={onSelectValue}
                            isDone={isDone}
                            dates={formattedDates}
                            displayFaresInCalendar={displayFaresInCalendar}
                            value={value}
                            minDate={minDate}
                            offerOneWay={offerOneWay}
                        />
                    )}
                </Fragment>
            );
        }}
    </ConnectedSegmentDateRoundTrip>
);
